import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Theme, styled } from "@mui/material/styles";
import { VendorRolloutBoardRowJSON } from "legal-entities/vendor-rollouts/_schema/vendor-rollout-board-schema";
import {
  StepTone,
  boardStageNames,
  notTrackedNotice,
  stageLabel,
  stageOf,
  startingStateLabel,
  stepDisplay,
  stepLabels,
} from "legal-entities/vendor-rollouts/_views/vendor-rollout-board-presentation";
import { Link as RouterLink } from "react-router";

const StageGroupHeader = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "notTracked",
})<{ notTracked: boolean }>(({ notTracked, theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  color: notTracked ? theme.palette.text.disabled : theme.palette.text.primary,
  textAlign: "center",
  whiteSpace: "nowrap",
}));
const StageGroupNote = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  display: "block",
  fontWeight: theme.typography.fontWeightRegular,
  maxWidth: "16rem",
  textAlign: "center",
  whiteSpace: "normal",
}));
const NotTrackedLabel = styled("div")({
  textTransform: "uppercase",
});
const StepHeader = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "startsGroup",
})<{ startsGroup: boolean }>(({ startsGroup, theme }) => ({
  borderLeft: startsGroup ? `1px solid ${theme.palette.divider}` : undefined,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightRegular,
  whiteSpace: "nowrap",
}));
/**
 * A stopped step is the one cell a reader is meant to stop at, so it takes the palette's warning
 * color rather than another shade of grey. Nothing rests on the color: the cell also prints a
 * different word and a sentence under it.
 */
const stepCellColor = (theme: Theme, tone: StepTone): string => {
  if (tone === "complete") return theme.palette.text.primary;
  if (tone === "stopped") return theme.palette.warning.dark;
  return theme.palette.text.disabled;
};
// The tone carries the meaning for a reader who does not distinguish the shades, because each one
// also prints a different sentence — the color only makes the scan faster.
const StepCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "tone" && prop !== "startsGroup",
})<{ tone: StepTone; startsGroup: boolean }>(({ startsGroup, theme, tone }) => ({
  borderLeft: startsGroup ? `1px solid ${theme.palette.divider}` : undefined,
  color: stepCellColor(theme, tone),
  fontStyle: tone === "pending" ? "italic" : undefined,
  // A stopped step carries a sentence under it, and a sentence on one line is a column as wide as
  // the reason. Every other cell holds two or three words and stays on its line.
  whiteSpace: tone === "stopped" ? "normal" : "nowrap",
}));
const StepNote = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  maxWidth: "14rem",
}));
const FirmName = styled("div")({
  fontWeight: 600,
});
const TeamLeaderTitle = styled("div")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

export type VendorRolloutStatusTableProps = {
  /**
   * Whether to name the client each roll-out belongs to. The staff board spans every client, so
   * without it two firms with the same name are indistinguishable; a client's own board would only
   * repeat their own name down a column.
   */
  readonly showClient?: boolean;
  readonly rows: readonly VendorRolloutBoardRowJSON[];
};

export default function VendorRolloutStatusTable(props: Readonly<VendorRolloutStatusTableProps>) {
  const rows = props.rows;
  const showClient = props.showClient ?? false;
  const stageNames = boardStageNames(rows);
  const notices = stageNames.map((stageName) => notTrackedNotice(rows, stageName));

  return (
    <Table size="small" aria-label="Firm onboarding status">
      <TableHead>
        <TableRow>
          {showClient && <TableCell rowSpan={2}>Client</TableCell>}
          <TableCell rowSpan={2}>Firm</TableCell>
          <TableCell rowSpan={2}>Team leader</TableCell>
          <TableCell rowSpan={2}>Starting point</TableCell>
          {stageNames.map((stageName, stageIndex) => {
            const notice = notices[stageIndex];
            return (
              <StageGroupHeader colSpan={2} key={stageName} notTracked={notice !== undefined}>
                {stageLabel(stageName)}
                {notice !== undefined && (
                  <StageGroupNote variant="caption">
                    <NotTrackedLabel>Not tracked yet</NotTrackedLabel>
                    {notice.reason ? <div>{notice.reason}</div> : null}
                    {notice.articleSlug ? (
                      // Attorney Hub's own help, not the reader's knowledge base. A plain
                      // /knowledge-base/<slug> looks the slug up in whichever organization the
                      // reader signed in as, and opens nothing — the notice explains a gap in the
                      // platform, so the article explaining it can only be Attorney Hub's.
                      <Link component={RouterLink} to={`/knowledge-base/attorney-hub/${notice.articleSlug}`}>
                        Read more
                      </Link>
                    ) : null}
                  </StageGroupNote>
                )}
              </StageGroupHeader>
            );
          })}
        </TableRow>
        <TableRow>
          {stageNames.map((stageName) => {
            const labels = stepLabels(stageName);
            return [
              <StepHeader key={`${stageName}-started`} startsGroup>
                {labels.started}
              </StepHeader>,
              <StepHeader key={`${stageName}-finished`} startsGroup={false}>
                {labels.finished}
              </StepHeader>,
            ];
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.rolloutId} data-rollout-id={row.rolloutId}>
            {showClient && <TableCell>{row.clientName}</TableCell>}
            <TableCell>
              <FirmName>{row.firmName}</FirmName>
            </TableCell>
            <TableCell>
              {row.teamLeaderName}
              <TeamLeaderTitle>{row.teamLeaderTitle}</TeamLeaderTitle>
            </TableCell>
            <TableCell>{startingStateLabel(row.startingState)}</TableCell>
            {stageNames.map((stageName) => {
              const stage = stageOf(row, stageName);
              // A row that does not carry this stage gets two empty cells rather than none, so the
              // remaining cells cannot slide left under the wrong headings.
              const started = stage ? stepDisplay(stage.started) : { text: "—", tone: "unknown" as StepTone };
              const finished = stage ? stepDisplay(stage.finished) : { text: "—", tone: "unknown" as StepTone };
              return [
                <StepCell key={`${row.rolloutId}-${stageName}-started`} startsGroup tone={started.tone}>
                  {started.text}
                  {started.note ? <StepNote variant="caption">{started.note}</StepNote> : null}
                </StepCell>,
                <StepCell key={`${row.rolloutId}-${stageName}-finished`} startsGroup={false} tone={finished.tone}>
                  {finished.text}
                  {finished.note ? <StepNote variant="caption">{finished.note}</StepNote> : null}
                </StepCell>,
              ];
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
